import { Box, Typography } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';

import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import BenchmarkKpiCard from 'views/benchmark-analytics/components/benchmark-kpi-card';
import { COMPANY_SLOT, MARKET_SLOT } from 'views/benchmark-analytics/constants/benchmark-series';
import { useBenchmarkFormatters } from 'views/benchmark-analytics/hooks/use-benchmark-formatters';
import { deltaLabelId, deltaSentiment } from 'views/benchmark-analytics/utils/benchmark-math';
import { GROWTH_DECIMALS } from '../constants/growth';
import { useGrowthState } from '../context/growth-context';

const ROOT = 'benchmarkAnalytics.growthPage';

const GrowthKpiSection: React.FC = () => {
  const intl = useIntl();
  const palette = useChartPalette();
  const { growth, market, filters } = useGrowthState();
  const { overall } = growth;
  const periods = growth.periods.length;
  const sentiment = deltaSentiment(overall.delta, GROWTH_DECIMALS);
  const { formatValue, formatDelta, formatCount, formatPeriodLabel } = useBenchmarkFormatters();
  const t = (id: string, values?: Record<string, string>) => intl.formatMessage({ id: `${ROOT}.${id}` }, values);

  const groupingLabel = t(`filters.${filters.grouping}`).toLocaleLowerCase(intl.locale);
  const deltaCaptionId = deltaLabelId(overall.delta, sentiment);
  const unit = t('unit.gPerDay');
  // Same x axis for both trends, so the tooltip names the period being hovered.
  const trend = (data: (number | null)[]) => ({
    data,
    xAxis: {
      data: growth.periods,
      scaleType: 'point' as const,
      valueFormatter: (key: string) => formatPeriodLabel(key, filters.grouping),
    },
    valueFormatter: (value: number | null) =>
      value === null ? formatValue(null, GROWTH_DECIMALS) : `${formatValue(value, GROWTH_DECIMALS)} ${unit}`,
  });

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Typography color={palette.text.secondary}>
        {t('caption', { market: formatCount(overall.marketRows), company: formatCount(overall.companyRows) })}
      </Typography>
      <Box display="grid" gap={4} gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }}>
        <BenchmarkKpiCard
          label={t('kpi.periods')}
          value={formatCount(periods)}
          dot="neutral"
          caption={t('kpi.periodsCaption', { count: formatCount(periods), grouping: groupingLabel })}
        />
        <BenchmarkKpiCard
          label={t('kpi.companyAvg')}
          value={formatValue(overall.company, GROWTH_DECIMALS)}
          dot={COMPANY_SLOT}
          caption={t('kpi.weightedOverall')}
          trend={trend(growth.company)}
        />
        <BenchmarkKpiCard
          label={t('kpi.marketAvg', { market: market.shortCode })}
          value={formatValue(overall.market, GROWTH_DECIMALS)}
          dot={MARKET_SLOT}
          caption={t('kpi.periodsMatched', { count: formatCount(overall.matchedPeriods) })}
          trend={trend(growth.market)}
        />
        <BenchmarkKpiCard
          label={t('kpi.overallDelta')}
          value={formatDelta(overall.delta, GROWTH_DECIMALS)}
          dot={sentiment}
          caption={deltaCaptionId ? intl.formatMessage({ id: deltaCaptionId }) : undefined}
        />
      </Box>
    </Box>
  );
};

export default GrowthKpiSection;
